import { useState } from 'react';
import { Activity, ArrowRight, BarChart3, ChevronRight, CircleDollarSign, Download, Headset, ListChecks, Plus, ShieldCheck, ShoppingCart, TrendingUp, Users } from 'lucide-react';
import { ColumnChart, CountUp } from '@ck/ui';
import { dateTimeLabel, usd } from '@ck/sdk';
import { A, api } from '../api';
import { canExport, downloadCsv, useMarket } from '../market';
import { Badge, Failed, Loading, useApi } from '../ui';

interface Overview {
  revenue30: number; revenueChangePct: number | null; orders30: number; awaiting: number; conversionPct: number | null;
  clients: number; clientsNewThisMonth: number; supportWaiting: number; slaPct: number;
  orderMix: { status: string; n: number }[]; governance: { label: string; pct: number }[]; governanceScore: number;
  recent: { kind: string; id: string; status: string; amount: string | number; at: string; who: string | null; what: string | null }[];
  queue: { label: string; n: number; page: string }[]; runRateMonthly: number; runRateYearly: number;
}
interface Series { days: string[]; revenue: number[]; sales: number[] }

const MIX: Record<string, { label: string; color: string }> = {
  paid: { label: 'Paid', color: 'var(--chart-2)' }, awaiting_review: { label: 'To confirm', color: 'var(--chart-3)' },
  pending_payment: { label: 'Not paid yet', color: 'var(--chart-4)' }, rejected: { label: 'Rejected', color: 'var(--chart-5)' },
  chargeback: { label: 'Chargeback', color: '#B23A48' }, cancelled: { label: 'Cancelled', color: 'var(--faint)' },
};
const short = (n: number) => (n >= 1e6 ? `$${(n / 1e6).toFixed(2).replace(/\.?0+$/, '')}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K` : usd(n, 0));
const count = (n: number) => (n >= 1e3 ? `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K` : String(n));

/** Ring of order statuses. */
function Donut({ parts }: { parts: { label: string; n: number; color: string }[] }) {
  const total = parts.reduce((s, p) => s + p.n, 0);
  const R = 52, C = 2 * Math.PI * R;
  let off = 0;
  return (
    <svg viewBox="0 0 140 140" className="donut" role="img" aria-label={parts.map((p) => `${p.label}: ${p.n}`).join(', ')}>
      <circle cx="70" cy="70" r={R} fill="none" stroke="var(--sunken)" strokeWidth="18" />
      {total > 0 && parts.filter((p) => p.n > 0).map((p) => {
        const len = (p.n / total) * C;
        const el = <circle key={p.label} cx="70" cy="70" r={R} fill="none" stroke={p.color} strokeWidth="18" strokeDasharray={`${Math.max(0, len - 2)} ${C}`} strokeDashoffset={-off} transform="rotate(-90 70 70)" />;
        off += len;
        return el;
      })}
      <text x="70" y="66" textAnchor="middle" className="donut-n">{count(total)}</text>
      <text x="70" y="84" textAnchor="middle" className="donut-l">orders</text>
    </svg>
  );
}

function Ring({ pct }: { pct: number }) {
  const R = 36, C = 2 * Math.PI * R;
  return (
    <svg viewBox="0 0 92 92" className="ring" role="img" aria-label={`${pct}%`}>
      <circle cx="46" cy="46" r={R} fill="none" stroke="var(--sunken)" strokeWidth="7" />
      <circle cx="46" cy="46" r={R} fill="none" stroke="var(--text)" strokeWidth="7" strokeLinecap="round" strokeDasharray={`${(pct / 100) * C} ${C}`} transform="rotate(-90 46 46)" />
      <text x="46" y="51" textAnchor="middle" className="ring-n">{pct}%</text>
    </svg>
  );
}

/** The top of the CRM dashboard: headline numbers, demand, order mix, activity, risk and the queue. */
export function Overview({ go }: { go: (p: string, param?: string) => void }) {
  const { market } = useMarket();
  const d = useApi(() => api.get<Overview>(A('/stats/overview')));
  const [days, setDays] = useState(30);
  const s = useApi(() => api.get<Series>(A(`/stats/series?days=${days}&scope=firm${market !== 'all' ? `&brand=${market}` : ''}`)), [days, market]);
  if (d.error) return <Failed error={d.error} retry={d.reload} />;
  if (!d.data) return <Loading />;
  const x = d.data;
  const parts = x.orderMix.map((m) => ({ label: MIX[m.status]?.label ?? m.status, color: MIX[m.status]?.color ?? 'var(--faint)', n: m.n })).sort((a, b) => b.n - a.n);
  const sum = (a: number[]) => a.reduce((p, q) => p + q, 0);
  const exportIt = () => downloadCsv('overview', [{ revenue_30d: x.revenue30, change_pct: x.revenueChangePct, paid_orders_30d: x.orders30, awaiting: x.awaiting, active_clients: x.clients, new_this_month: x.clientsNewThisMonth, support_sla_pct: x.slaPct, run_rate_month: x.runRateMonthly }]);
  return (
    <div className="ov">
      <div className="ov-bar">
        <nav className="crumbs" aria-label="Breadcrumb"><span>Dashboard</span><ChevronRight /><b>Operations overview</b></nav>
        <div className="row-a">
          <button className="btn sm" onClick={() => go('plans')}><Plus />New challenge</button>
          {canExport() && <button className="btn ghost sm" onClick={exportIt}><Download />Export</button>}
          <button className="btn ghost sm" onClick={() => go('firm-security')}><ShieldCheck />System health</button>
        </div>
      </div>

      <div className="kpis">
        <button className="kpi" onClick={() => go('orders', 'paid')}><span><small>Revenue (30 days)</small><b><CountUp value={short(x.revenue30)} /></b><em className={x.revenueChangePct !== null && x.revenueChangePct < 0 ? 'neg' : ''}>{x.revenueChangePct === null ? 'No sales in the previous 30 days' : `${x.revenueChangePct > 0 ? '+' : ''}${x.revenueChangePct}% vs prior period`}</em></span><CircleDollarSign /></button>
        <button className="kpi" onClick={() => go('orders')}><span><small>Orders (30 days)</small><b><CountUp value={count(x.orders30)} /></b><em>{x.awaiting} awaiting confirmation</em></span><ShoppingCart /></button>
        <button className="kpi" onClick={() => go('traders')}><span><small>Active clients</small><b><CountUp value={count(x.clients)} /></b><em>{x.clientsNewThisMonth} new this month</em></span><Users /></button>
        <button className="kpi" onClick={() => go('support')}><span><small>Support SLA</small><b>{x.slaPct}%</b><em>{x.supportWaiting} waiting for a reply</em></span><Headset /></button>
      </div>

      <div className="ov-grid">
        <section className="panel ov-demand">
          <div className="panel-h"><div><h3><BarChart3 />Revenue and demand</h3><p className="muted small">Challenge revenue per day{market !== 'all' ? ' in this market' : ''}</p></div>
            <div className="seg sm" role="group">{[14, 30, 90].map((n) => <button key={n} className={days === n ? 'on' : ''} onClick={() => setDays(n)}>{n}d</button>)}</div></div>
          {s.data ? <ColumnChart label="Revenue per day" days={s.data.days} values={s.data.revenue} color="var(--chart-1)" format={(v) => short(v)} /> : <Loading />}
          <div className="ov-figs">
            <div><b>{short(sum(s.data?.revenue ?? []))}</b><small>Revenue</small></div>
            <div><b>{count(sum(s.data?.sales ?? []))}</b><small>Challenges sold</small></div>
            <div><b>{x.conversionPct === null ? '—' : `${x.conversionPct}%`}</b><small>Checkout conversion</small></div>
          </div>
        </section>
        <section className="panel ov-mix">
          <div className="panel-h"><div><h3><ShoppingCart />Order status</h3><p className="muted small">Orders in the last 90 days by stage</p></div></div>
          <Donut parts={parts} />
          <ul className="legend">{parts.map((p) => <li key={p.label}><i style={{ background: p.color }} /><span>{p.label}</span><b>{p.n.toLocaleString()}</b></li>)}</ul>
          <div className="panel-f"><button className="link" onClick={() => go('orders', 'all')}>Open orders<ArrowRight /></button></div>
        </section>
      </div>

      <div className="ov-grid">
        <section className="panel ov-recent">
          <div className="panel-h"><h3><Activity />Recent commerce activity</h3><button className="link" onClick={() => go('orders', 'all')}>All orders</button></div>
          {x.recent.length === 0 ? <p className="muted">Orders and payouts appear here as they happen.</p> : (
            <div className="tw flat"><table className="tbl">
              <thead><tr><th>Record</th><th>Client</th><th>Status</th><th className="r">Amount</th><th /></tr></thead>
              <tbody>{x.recent.map((r) => (
                <tr key={`${r.kind}${r.id}`}>
                  <th>{r.kind === 'order' ? `Order #${r.id.slice(0, 6).toUpperCase()}` : `Payout #${r.id.slice(0, 6).toUpperCase()}`}<small className="block muted">{r.kind === 'order' ? r.what : r.what === 'partner' ? 'Affiliate commission' : 'Trader reward'} · {dateTimeLabel(r.at)}</small></th>
                  <td>{r.who ?? '—'}</td><td><Badge status={r.status} /></td><td className="r"><b>{usd(Number(r.amount), 0)}</b></td>
                  <td className="r"><button className="link" onClick={() => go(r.kind === 'order' ? 'orders' : 'payouts', r.kind === 'order' ? 'all' : undefined)}>Open</button></td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </section>
        <section className="panel ov-gov">
          <div className="gov-h"><div><h3><ShieldCheck />Governance and risk</h3><p className="muted small">Security, payouts, identity checks and support standards</p></div><Ring pct={x.governanceScore} /></div>
          <ul className="gov">{x.governance.map((g) => <li key={g.label}><span>{g.label}</span><b>{g.pct}%</b><i><s style={{ width: `${g.pct}%` }} /></i></li>)}</ul>
          <div className="panel-f"><button className="link" onClick={() => go('firm-security')}>Review controls<ArrowRight /></button></div>
        </section>
      </div>

      <div className="ov-grid">
        <section className="panel ov-queue">
          <div className="panel-h"><h3><ListChecks />Priority queue</h3><span className="pill">{x.queue.reduce((n, q) => n + q.n, 0)} waiting</span></div>
          <ul className="queue">{x.queue.map((q) => (
            <li key={q.label}><button onClick={() => go(q.page, q.label === 'Refunds due' ? 'refunds' : undefined)} className={q.n ? 'hot' : ''}><span>{q.label}</span><b>{q.n}</b><ArrowRight /></button></li>
          ))}</ul>
        </section>
        <section className="panel ov-run">
          <div className="panel-h"><h3><TrendingUp />Revenue run rate</h3><span className="run-pill">{short(x.runRateYearly)} a year</span></div>
          <p>At the last 30 days’ pace, CK brings in <b>{short(x.runRateMonthly)}</b> a month from challenge sales.</p>
          <div className="run-figs"><div><b>{short(x.runRateMonthly)}</b><small>Monthly</small></div><div><b>{short(x.runRateYearly)}</b><small>Yearly</small></div><div><b>{x.revenueChangePct === null ? '—' : `${x.revenueChangePct > 0 ? '+' : ''}${x.revenueChangePct}%`}</b><small>Month on month</small></div></div>
        </section>
      </div>
    </div>
  );
}
